import { ConfigurableProperty } from 'api/digitalTwin/digitalTwin.api'
import type { SettingSource } from 'api/digitalTwin/digitalTwin.types'
import { Options } from 'helpers/filterComponent.helper/filterComponent.helper'
import { useMemo } from 'react'

export const DEFAULT_SOURCES: SettingSource[] = ['app', 'external_api']
const SOURCE_KEY = 'source'
const SOURCE_PREFIX = `${SOURCE_KEY}/`
const SOURCE_TAGS = DEFAULT_SOURCES.map((source) => `${SOURCE_PREFIX}${source}`)

type UseSourceFilterParams = {
  allConfigurableProperties: ConfigurableProperty[]
  propertyTags: string[]
  filters: Options
  sourceFilterEnabled: boolean
  tagSortingOrder?: string[]
}

export function useSourceFilter({
  allConfigurableProperties,
  propertyTags,
  filters,
  sourceFilterEnabled,
  tagSortingOrder,
}: UseSourceFilterParams): {
  availableFiltersSubset: string[][]
  topLevelSortingOrderOverride: string[] | undefined
} {
  const availableFiltersSubset = useMemo(() => {
    const propertyFilters = allConfigurableProperties.map(
      (p) => p.tags?.filter((tag) => propertyTags.includes(tag)) ?? []
    )

    if (!sourceFilterEnabled) {
      return propertyFilters
    }

    return [...propertyFilters, SOURCE_TAGS]
  }, [allConfigurableProperties, propertyTags, sourceFilterEnabled])

  const topLevelSortingOrderOverride = useMemo(() => {
    if (!sourceFilterEnabled) {
      return undefined
    }

    const availableTopLevels = Object.keys(filters).filter((topLevel) => topLevel !== SOURCE_KEY)
    const configuredOrder = tagSortingOrder ?? []
    const configuredAvailable = configuredOrder.filter(
      (topLevel) => topLevel !== SOURCE_KEY && availableTopLevels.includes(topLevel)
    )
    const remainingTopLevels = availableTopLevels
      .filter((topLevel) => !configuredAvailable.includes(topLevel))
      .sort((a, b) => a.localeCompare(b))

    return [...configuredAvailable, ...remainingTopLevels, SOURCE_KEY]
  }, [sourceFilterEnabled, filters, tagSortingOrder])

  return {
    availableFiltersSubset,
    topLevelSortingOrderOverride,
  }
}

export function getSelectedSources(filterTags: Set<string>, sourceFilterEnabled: boolean): SettingSource[] {
  if (!sourceFilterEnabled) {
    return DEFAULT_SOURCES
  }

  const sourceTags = [...filterTags].filter((tag) => tag.startsWith(SOURCE_PREFIX))
  const selected = sourceTags
    .map((tag) => tag.split('/')[1])
    .filter((source): source is SettingSource => source === 'app' || source === 'external_api')

  return selected.length > 0 ? selected : DEFAULT_SOURCES
}

export function filterBySource<T extends { source?: SettingSource }>(
  items: T[],
  sourceFilterEnabled: boolean | undefined,
  selectedSources: SettingSource[]
): T[] {
  if (!sourceFilterEnabled) {
    return items
  }

  const sources = selectedSources.length > 0 ? selectedSources : DEFAULT_SOURCES
  return items.filter((item) => sources.includes(item.source ?? 'app'))
}
